import { useState } from 'react';
import { Link } from 'react-router-dom';

interface PriceCategory {
  category: string;
  items: {
    label: string;
    min: number;
    max?: number;
    unit: string;
    note?: string;
  }[];
}

interface WorkStep {
  step: string;
  desc: string;
}

interface ProcessItem {
  id: string;
  name: string;
  icon: string;
  color: string;
  bgColor: string;
  duration: string;
  summary: string;
  description: string;
  priceCategories: PriceCategory[];
  steps: WorkStep[];
  caution: string;
  img: string;
}

const PROCESSES: ProcessItem[] = [
  {
    id: 'demolition',
    name: '철거 · 구조',
    icon: 'ri-hammer-line',
    color: '#8B7355',
    bgColor: '#8B735510',
    duration: '2~5일',
    summary: '기존 마감재를 제거하고 새로운 시공을 위한 기반을 만드는 첫 번째 공정',
    description: '철거 공정은 인테리어의 시작점입니다. 기존 바닥재, 벽지, 천장 마감재를 제거하고 필요에 따라 비내력벽을 철거하여 공간을 재구성합니다. 철거 후 구조 상태를 점검하고 보수가 필요한 부분을 사전에 파악합니다.',
    priceCategories: [
      {
        category: '바닥재 · 벽체 철거',
        items: [
          { label: '강마루 / 원목마루 철거', min: 3, max: 5, unit: '만원/평', note: '접착제 제거 포함' },
          { label: '장판 철거', min: 1, max: 2, unit: '만원/평', note: '하지 상태에 따라 상이' },
          { label: '타일 바닥 철거', min: 5, max: 8, unit: '만원/평', note: '두께·접착 방식에 따라 상이' },
          { label: '도배 제거 (합지/실크)', min: 1, max: 3, unit: '만원/평', note: '초배지 포함' },
          { label: '타일 벽체 철거', min: 6, max: 10, unit: '만원/평', note: '욕실·주방 벽 기준' },
        ],
      },
      {
        category: '욕실 · 주방 · 구조 철거',
        items: [
          { label: '욕실 전체 철거 (소형)', min: 60, max: 100, unit: '만원/개소', note: '1.5평 기준, 도기·타일 포함' },
          { label: '욕실 전체 철거 (중형)', min: 100, max: 150, unit: '만원/개소', note: '2~3평 기준' },
          { label: '주방 싱크대 철거', min: 30, max: 60, unit: '만원/식', note: '상·하부장 포함' },
          { label: '비내력벽 철거 (경량)', min: 30, max: 60, unit: '만원/개소', note: '석고보드 벽체 기준' },
          { label: '비내력벽 철거 (조적)', min: 60, max: 120, unit: '만원/개소', note: '벽돌·블록 벽체 기준' },
        ],
      },
      {
        category: '창호 · 폐기물 처리',
        items: [
          { label: '시스템창 철거 (소형)', min: 10, max: 20, unit: '만원/개소', note: '1.2m 이하 기준' },
          { label: '시스템창 철거 (대형)', min: 20, max: 40, unit: '만원/개소', note: '1.2m 초과 기준' },
          { label: '현관문 철거', min: 20, max: 40, unit: '만원/개소', note: '방화문 포함' },
          { label: '폐기물 처리 (30평)', min: 50, max: 80, unit: '만원/식', note: '1톤 트럭 2~3대 기준' },
          { label: '엘리베이터 보양 설치', min: 10, max: 20, unit: '만원/식', note: '공사 기간 전체 기준' },
        ],
      },
    ],
    steps: [
      { step: '현장 조사', desc: '구조 파악 및 철거 범위 확정' },
      { step: '보양 작업', desc: '엘리베이터·복도 보호재 설치' },
      { step: '철거 시공', desc: '마감재 순차적 제거' },
      { step: '폐기물 반출', desc: '당일 폐기물 처리 완료' },
    ],
    caution: '비내력벽 여부는 반드시 구조 전문가 확인 후 진행합니다. 관리사무소 사전 신고 필수.',
    img: 'https://public.readdy.ai/ai/img_res/61479635d6b6ceb4d880d66d778b961a.jpg',
  },
  {
    id: 'bathroom',
    name: '욕실 공사',
    icon: 'ri-drop-line',
    color: '#4A7C8E',
    bgColor: '#4A7C8E10',
    duration: '5~10일',
    summary: '방수부터 타일, 위생도기까지 욕실 전체를 새롭게 시공하는 핵심 공정',
    description: '욕실 공사는 방수층 재시공을 기본으로 타일, 위생도기, 수전, 환기 시스템까지 전면 교체합니다. 누수 방지를 위한 방수 공사가 가장 중요하며, 고급 포세린 타일과 브랜드 위생도기로 호텔급 욕실을 완성합니다.',
    priceCategories: [
      {
        category: '욕실 전체 · 방수',
        items: [
          { label: '욕실 전체 리모델링 (소형)', min: 150, max: 250, unit: '만원/개소', note: '1.5평, 방수+타일+도기 포함' },
          { label: '욕실 전체 리모델링 (중형)', min: 250, max: 400, unit: '만원/개소', note: '2~3평 기준' },
          { label: '욕실 방수 (소형)', min: 40, max: 70, unit: '만원/개소', note: '우레탄 2중 방수' },
          { label: '욕실 방수 (중형)', min: 70, max: 110, unit: '만원/개소', note: '2~3평 기준' },
        ],
      },
      {
        category: '타일 시공',
        items: [
          { label: '욕실 타일 전체 시공 (소형)', min: 80, max: 130, unit: '만원/개소', note: '바닥+벽 포함' },
          { label: '욕실 타일 전체 시공 (중형)', min: 130, max: 200, unit: '만원/개소', note: '2~3평 기준' },
          { label: '포세린 타일 (600×1200)', min: 15, max: 25, unit: '만원/평', note: '대형 타일 시공비 포함' },
          { label: '타일만 교체 (기존 도기 유지)', min: 100, max: 180, unit: '만원/개소', note: '바닥+벽 타일' },
        ],
      },
      {
        category: '위생기기 · 수전',
        items: [
          { label: '양변기 교체', min: 30, max: 70, unit: '만원/개', note: '자재비 포함, 절수형' },
          { label: '세면대 세트 교체', min: 40, max: 80, unit: '만원/개소', note: '수전·거울장 포함' },
          { label: '샤워 부스 설치', min: 50, max: 120, unit: '만원/개소', note: '프레임리스 강화유리 기준' },
          { label: '욕실 수전 교체', min: 8, max: 20, unit: '만원/개', note: '자재비 별도, 시공비' },
          { label: '배수 배관 교체', min: 30, max: 70, unit: '만원/개소', note: '욕실 1개소 기준' },
        ],
      },
    ],
    steps: [
      { step: '기존 철거', desc: '타일·도기 전체 제거' },
      { step: '방수 시공', desc: '2중 방수층 도포 및 양생' },
      { step: '타일 시공', desc: '바닥·벽 타일 붙임 작업' },
      { step: '도기·수전 설치', desc: '위생도기 및 수전 설치 완료' },
    ],
    caution: '방수 양생 기간(최소 24시간) 준수 필수. 타일 줄눈 시공 후 48시간 이상 건조 후 사용 권장.',
    img: 'https://readdy.ai/api/search-image?query=luxury%20Korean%20bathroom%20renovation%20completed%2C%20large%20format%20marble%20porcelain%20tiles%2C%20modern%20freestanding%20bathtub%2C%20frameless%20glass%20shower%20enclosure%2C%20premium%20chrome%20fixtures%2C%20warm%20ambient%20lighting%2C%20spa%20atmosphere%2C%20clean%20sophisticated%20design&width=800&height=500&seq=proc_bath&orientation=landscape',
  },
  {
    id: 'kitchen',
    name: '주방 공사',
    icon: 'ri-restaurant-line',
    color: '#7A6E5F',
    bgColor: '#7A6E5F10',
    duration: '4~8일',
    summary: '맞춤 제작 싱크대와 수납 시스템으로 기능적이고 아름다운 주방을 완성',
    description: '주방 공사는 싱크대 맞춤 제작을 중심으로 타일, 수전, 빌트인 가전 설치까지 포함합니다. 주방 동선을 분석하여 최적의 레이아웃을 설계하고, 내구성 높은 상판 소재와 수납 효율을 극대화한 가구 배치로 실용적인 주방을 만들어 드립니다.',
    priceCategories: [
      {
        category: '싱크대 · 가구',
        items: [
          { label: '싱크대 전체 교체 (일자형)', min: 200, max: 350, unit: '만원/식', note: '2400mm 기준, 상판 포함' },
          { label: '싱크대 전체 교체 (ㄱ자형)', min: 300, max: 500, unit: '만원/식', note: '2700mm 이상 기준' },
          { label: '상판만 교체 (세라믹)', min: 80, max: 150, unit: '만원/식', note: '기존 하부장 유지' },
          { label: '상판만 교체 (석영석)', min: 60, max: 120, unit: '만원/식', note: '기존 하부장 유지' },
        ],
      },
      {
        category: '타일 · 수전',
        items: [
          { label: '주방 뒷벽 타일 시공', min: 12, max: 20, unit: '만원/평', note: '헤링본 패턴 포함' },
          { label: '주방 수전 교체', min: 10, max: 25, unit: '만원/개', note: '자재비 별도, 시공비' },
          { label: '주방 배수 라인 교체', min: 20, max: 40, unit: '만원/개소', note: '싱크대 하부 포함' },
          { label: '현관 타일 시공', min: 12, max: 22, unit: '만원/평', note: '포세린·천연석 선택' },
        ],
      },
      {
        category: '빌트인 가전 · 설비',
        items: [
          { label: '빌트인 오븐 설치', min: 20, max: 50, unit: '만원', note: '전기 연결 포함' },
          { label: '식기세척기 설치', min: 15, max: 35, unit: '만원', note: '배수 연결 포함' },
          { label: '가스레인지 → 인덕션 교체', min: 20, max: 40, unit: '만원', note: '전기 용량 확인 필수' },
          { label: '주방 후드 교체', min: 15, max: 40, unit: '만원', note: '덕트 연결 포함' },
        ],
      },
    ],
    steps: [
      { step: '기존 철거', desc: '싱크대·타일 제거' },
      { step: '배관·배선 정리', desc: '수도·전기 라인 재정비' },
      { step: '타일 시공', desc: '뒷벽 타일 붙임' },
      { step: '가구·가전 설치', desc: '싱크대 설치 및 가전 연결' },
    ],
    caution: '가스 배관 작업은 반드시 공인 가스 기술자가 시공합니다. 빌트인 가전은 사전 치수 확인 필수.',
    img: 'https://readdy.ai/api/search-image?query=modern%20custom%20Korean%20kitchen%20renovation%20completed%2C%20sleek%20white%20handleless%20cabinetry%2C%20quartz%20countertop%20island%2C%20integrated%20appliances%2C%20pendant%20lighting%2C%20herringbone%20backsplash%20tile%2C%20clean%20minimalist%20design%2C%20functional%20elegant%20cooking%20space&width=800&height=500&seq=proc_kit&orientation=landscape',
  },
  {
    id: 'flooring',
    name: '바닥 공사',
    icon: 'ri-layout-bottom-line',
    color: '#6B5B45',
    bgColor: '#6B5B4510',
    duration: '2~4일',
    summary: '강마루, 타일, 장판 등 다양한 바닥재로 공간의 분위기를 결정하는 공정',
    description: '바닥재는 공간의 전체적인 분위기를 좌우하는 중요한 요소입니다. 강마루, 포세린 타일, 헤링본 패턴, 비닐 장판 등 다양한 소재 중 공간 특성과 예산에 맞는 최적의 바닥재를 제안합니다. 하지 작업부터 마감까지 꼼꼼하게 시공합니다.',
    priceCategories: [
      {
        category: '마루 · 장판',
        items: [
          { label: '강마루 시공', min: 10, max: 15, unit: '만원/평', note: '자재비 포함, 오크 기준' },
          { label: '원목마루 시공', min: 18, max: 30, unit: '만원/평', note: '자재비 포함, 고급형' },
          { label: '헤링본 마루 시공', min: 18, max: 28, unit: '만원/평', note: '패턴 시공 추가비 포함' },
          { label: '장판 시공', min: 5, max: 8, unit: '만원/평', note: '방 전용, 자재비 포함' },
          { label: '데코타일 시공', min: 6, max: 10, unit: '만원/평', note: '사무실·다용도실 추천' },
        ],
      },
      {
        category: '타일 바닥',
        items: [
          { label: '포세린 타일 바닥 (600×600)', min: 15, max: 25, unit: '만원/평', note: '자재비 포함' },
          { label: '발코니 타일 시공', min: 10, max: 18, unit: '만원/평', note: '미끄럼 방지 타일 기준' },
          { label: '타일 줄눈 재시공', min: 3, max: 6, unit: '만원/평', note: '에폭시 줄눈 기준' },
        ],
      },
      {
        category: '하지 · 마감',
        items: [
          { label: '셀프 레벨링 (바닥 평탄화)', min: 3, max: 6, unit: '만원/평', note: '단차 10mm 이하 기준' },
          { label: '방습 처리', min: 2, max: 4, unit: '만원/평', note: '1층·지하 필수' },
          { label: '걸레받이 설치', min: 0.5, max: 1, unit: '만원/m', note: 'MDF·PVC 선택' },
          { label: '문틀 하부 컷팅', min: 1, max: 2, unit: '만원/개', note: '바닥재 두께 조정' },
        ],
      },
    ],
    steps: [
      { step: '기존 바닥 철거', desc: '기존 마감재 제거 및 하지 정리' },
      { step: '하지 작업', desc: '레벨링 및 방습 처리' },
      { step: '바닥재 시공', desc: '선택 자재 시공' },
      { step: '걸레받이 설치', desc: '몰딩 및 마감 처리' },
    ],
    caution: '타일 시공 후 최소 24시간 양생 필요. 강마루는 시공 전 자재를 현장에서 24시간 이상 보관 후 시공.',
    img: 'https://readdy.ai/api/search-image?query=premium%20flooring%20installation%20Korean%20apartment%2C%20beautiful%20herringbone%20wood%20pattern%20floor%2C%20professional%20craftsman%20laying%20tiles%2C%20clean%20bright%20interior%20space%2C%20warm%20natural%20wood%20tones%2C%20elegant%20minimalist%20design%2C%20high%20quality%20materials&width=800&height=500&seq=proc_floor&orientation=landscape',
  },
  {
    id: 'painting',
    name: '도장 · 도배',
    icon: 'ri-paint-brush-line',
    color: '#9B7B6B',
    bgColor: '#9B7B6B10',
    duration: '2~5일',
    summary: '벽지와 페인트로 공간의 색감과 질감을 완성하는 마감 공정',
    description: '도배와 도장은 인테리어의 마지막 마감 단계로, 공간의 분위기를 완성합니다. 합지, 실크, 천연 소재 등 다양한 벽지와 친환경 페인트를 사용하며, 스터코, 마이크로시멘트 등 고급 질감 마감도 제공합니다.',
    priceCategories: [
      {
        category: '도배 (벽지)',
        items: [
          { label: '합지 도배 (전체)', min: 4, max: 6, unit: '만원/평', note: '자재비 포함, 30평 기준' },
          { label: '실크 도배 (전체)', min: 7, max: 10, unit: '만원/평', note: '고급 실크 벽지 기준' },
          { label: '천연 소재 벽지', min: 12, max: 20, unit: '만원/평', note: '황토·한지·삼베 등' },
          { label: '포인트 벽지 시공', min: 15, max: 30, unit: '만원/면', note: '1개 면 기준, 자재비 포함' },
        ],
      },
      {
        category: '페인트 도장',
        items: [
          { label: '벽 페인트 도장 (전체)', min: 6, max: 9, unit: '만원/평', note: '친환경 수성 페인트' },
          { label: '천장 페인트 도장', min: 4, max: 7, unit: '만원/평', note: '화이트 무광 기준' },
          { label: '문틀·몰딩 도장', min: 20, max: 40, unit: '만원/식', note: '30평 전체 기준' },
          { label: '외부 발코니 도장', min: 5, max: 9, unit: '만원/평', note: '방수 페인트 기준' },
        ],
      },
      {
        category: '고급 질감 마감',
        items: [
          { label: '스터코 마감', min: 12, max: 18, unit: '만원/평', note: '유럽식 질감 마감' },
          { label: '마이크로시멘트', min: 20, max: 35, unit: '만원/평', note: '욕실·주방 적용 가능' },
          { label: '우드 패널 시공', min: 15, max: 28, unit: '만원/평', note: '포인트 벽 우드 마감' },
          { label: '퍼티 작업 (하지)', min: 2, max: 4, unit: '만원/평', note: '도배·도장 전 하지 처리' },
        ],
      },
    ],
    steps: [
      { step: '면 정리', desc: '기존 벽지 제거 및 퍼티 작업' },
      { step: '초배지 시공', desc: '초배지 부착 및 건조' },
      { step: '도배·도장', desc: '선택 마감재 시공' },
      { step: '마감 점검', desc: '기포·들뜸 여부 최종 확인' },
    ],
    caution: '도배 후 최소 2~3일 환기 필수. 페인트 도장 시 VOC 저함량 친환경 제품 사용.',
    img: 'https://readdy.ai/api/search-image?query=professional%20wallpaper%20installation%20Korean%20apartment%20interior%2C%20craftsman%20applying%20premium%20silk%20wallpaper%2C%20clean%20bright%20room%2C%20warm%20neutral%20tones%2C%20elegant%20texture%2C%20high%20quality%20finishing%20work%2C%20modern%20minimalist%20space&width=800&height=500&seq=proc_paint&orientation=landscape',
  },
  {
    id: 'electric',
    name: '전기 · 조명',
    icon: 'ri-flashlight-line',
    color: '#B8860B',
    bgColor: '#B8860B10',
    duration: '2~4일',
    summary: '안전한 전기 배선과 감각적인 조명 설계로 공간의 완성도를 높이는 공정',
    description: '노후 전기 배선 교체와 조명 설계는 안전과 미관 모두를 위한 필수 공정입니다. 콘센트 위치 재배치, 간접 조명 설치, 스마트홈 시스템 연동까지 현대적인 전기 환경을 구축합니다. 모든 전기 공사는 전기 기술자 자격증 보유자가 시공합니다.',
    priceCategories: [
      {
        category: '전기 배선 · 분전반',
        items: [
          { label: '전기 배선 전체 교체', min: 8, max: 14, unit: '만원/평', note: '분전반 포함, 30평 기준' },
          { label: '분전반 교체 (20회로)', min: 30, max: 60, unit: '만원/대', note: '누전차단기 포함' },
          { label: '분전반 교체 (30회로)', min: 50, max: 90, unit: '만원/대', note: '대형 평수용' },
          { label: '콘센트 추가 설치', min: 3, max: 6, unit: '만원/개', note: '배선 연장 포함' },
          { label: '전기 용량 증설', min: 50, max: 120, unit: '만원/식', note: '한전 신청 포함' },
        ],
      },
      {
        category: '조명 기구 설치',
        items: [
          { label: '매입형 LED 다운라이트', min: 2, max: 4, unit: '만원/개', note: '6인치 15W 기준, 시공비' },
          { label: '간접 조명 (코브 조명)', min: 3, max: 6, unit: '만원/m', note: 'LED 바 + 몰딩 포함' },
          { label: '펜던트 조명 설치', min: 3, max: 8, unit: '만원/개', note: '배선 연결 시공비' },
          { label: '주방 하부장 조명', min: 10, max: 20, unit: '만원/식', note: 'LED 바 + 센서 포함' },
        ],
      },
      {
        category: '스위치 · 스마트홈',
        items: [
          { label: '터치 스위치 교체', min: 3, max: 8, unit: '만원/개', note: '자재비 포함' },
          { label: '스마트 스위치 설치', min: 5, max: 12, unit: '만원/개', note: '와이파이 연동형' },
          { label: '스마트홈 기본 패키지', min: 80, max: 150, unit: '만원/식', note: '조명·난방 제어 포함' },
          { label: '스마트홈 풀 패키지', min: 150, max: 300, unit: '만원/식', note: '조명·난방·보안·가전 연동' },
          { label: '월패드 교체', min: 30, max: 80, unit: '만원/대', note: '인터폰·난방 통합 제어' },
        ],
      },
    ],
    steps: [
      { step: '배선 설계', desc: '콘센트·스위치 위치 확정' },
      { step: '배선 공사', desc: '전선 교체 및 배관 작업' },
      { step: '기구 설치', desc: '조명·콘센트·스위치 설치' },
      { step: '점검 및 테스트', desc: '누전 차단기 테스트 완료' },
    ],
    caution: '전기 공사는 반드시 전기 기술자 자격증 보유자가 시공합니다. 분전반 용량 사전 확인 필수.',
    img: 'https://readdy.ai/api/search-image?query=elegant%20interior%20lighting%20design%20Korean%20apartment%2C%20warm%20indirect%20LED%20cove%20lighting%2C%20pendant%20lights%20over%20dining%20table%2C%20recessed%20ceiling%20lights%2C%20layered%20lighting%20atmosphere%2C%20modern%20minimalist%20living%20room%2C%20sophisticated%20ambiance%2C%20golden%20warm%20tones&width=800&height=500&seq=proc_elec&orientation=landscape',
  },
  {
    id: 'carpentry',
    name: '목공 · 창호',
    icon: 'ri-tools-line',
    color: '#6B8E6B',
    bgColor: '#6B8E6B10',
    duration: '6~14일',
    summary: '공간의 뼈대를 완성하는 목공 작업과 단열·방음 성능을 결정하는 창호 교체를 함께 진행하는 핵심 공정',
    description: '목공 공정은 경량 칸막이 벽체, 천장 석고보드, 붙박이장 제작, 문틀 설치 등 인테리어의 구조적 뼈대를 만드는 단계입니다. 이와 함께 창호(시스템창, 현관문, 실내문, 중문) 교체를 병행하여 단열·방음 성능을 크게 향상시킵니다.',
    priceCategories: [
      {
        category: '벽체 · 천장 목공',
        items: [
          { label: '경량 벽체 신설', min: 8, max: 14, unit: '만원/평', note: '석고보드 2겹 기준' },
          { label: '천장 석고보드 시공', min: 6, max: 10, unit: '만원/평', note: '경량 틀 포함' },
          { label: '단열재 시공 (내벽)', min: 4, max: 8, unit: '만원/평', note: '글라스울·압출법 선택' },
          { label: '방음재 시공', min: 6, max: 12, unit: '만원/평', note: '층간·세대간 방음' },
        ],
      },
      {
        category: '붙박이장 · 수납',
        items: [
          { label: '붙박이장 (2400H 기준)', min: 100, max: 200, unit: '만원/개소', note: '폭 1.2m 기준, 맞춤 제작' },
          { label: '드레스룸 전체 시공', min: 200, max: 400, unit: '만원/식', note: '3~4평 기준' },
          { label: '팬트리 수납장', min: 80, max: 150, unit: '만원/개소', note: '주방 인접 수납 공간' },
          { label: '신발장 맞춤 제작', min: 60, max: 120, unit: '만원/개소', note: '현관 맞춤 기준' },
        ],
      },
      {
        category: '시스템창 · 문',
        items: [
          { label: '일반 이중창', min: 40, max: 70, unit: '만원/개소', note: '기본 단열 성능' },
          { label: '로이유리 이중창', min: 60, max: 100, unit: '만원/개소', note: '단열·자외선 차단 강화' },
          { label: '방음 삼중창', min: 100, max: 160, unit: '만원/개소', note: '방음 최고 등급, 도로변 추천' },
          { label: '현관 방화문 교체', min: 120, max: 200, unit: '만원/개소', note: '스마트 도어락 포함' },
          { label: '실내 중문 (슬라이딩)', min: 60, max: 120, unit: '만원/개소', note: '강화유리·우드 선택' },
          { label: '포켓도어 설치', min: 100, max: 180, unit: '만원/개소', note: '벽 매립형, 공간 절약' },
        ],
      },
    ],
    steps: [
      { step: '창호 발주', desc: '치수 실측 후 제작 발주 (7~10일 소요)' },
      { step: '목공 착공', desc: '경량 벽체·천장 틀 작업 시작' },
      { step: '석고보드 시공', desc: '벽·천장 석고보드 부착 및 테이핑' },
      { step: '붙박이장 제작', desc: '현장 맞춤 붙박이장 설치' },
      { step: '창호 설치', desc: '시스템창·현관문·중문 설치 및 실링' },
      { step: '문틀·몰딩 마감', desc: '문틀 설치 및 몰딩 마감 처리' },
    ],
    caution: '창호 교체는 관리사무소 사전 신고 필수. 시스템창 발주 후 제작 기간(7~10일)이 있으므로 공정 시작 전 미리 주문하세요.',
    img: 'https://readdy.ai/api/search-image?query=carpentry%20and%20window%20installation%20Korean%20apartment%20renovation%2C%20workers%20installing%20system%20window%20aluminum%20frame%20and%20gypsum%20board%20ceiling%20simultaneously%2C%20professional%20construction%20site%2C%20bright%20clean%20workspace%2C%20modern%20renovation%20process&width=800&height=500&seq=proc_carp_win&orientation=landscape',
  },
  {
    id: 'window',
    name: '창호 · 문 (상세)',
    icon: 'ri-door-open-line',
    color: '#7B6B8B',
    bgColor: '#7B6B8B10',
    duration: '1~3일',
    summary: '이중창·삼중창·로이유리 등 창호 종류별 특성과 현관문·중문·포켓도어까지 상세 옵션 안내',
    description: '창호는 단열·방음·보안 성능을 결정하는 핵심 요소입니다. 일반 이중창부터 로이(Low-E) 유리 이중창, 방음 삼중창까지 목적에 맞는 창호를 선택할 수 있습니다. 현관 방화문은 보안 등급과 디자인을 함께 고려하고, 실내 중문과 포켓도어는 공간 분리와 동선 효율을 높여줍니다.',
    priceCategories: [
      {
        category: '시스템창 (단열 등급별)',
        items: [
          { label: '일반 이중창', min: 40, max: 70, unit: '만원/개소', note: '기본 단열 성능' },
          { label: '로이유리 이중창', min: 60, max: 100, unit: '만원/개소', note: '단열 40% 향상, UV 차단 99%' },
          { label: '방음 삼중창', min: 100, max: 160, unit: '만원/개소', note: '방음 최고 등급, 도로변 추천' },
          { label: '발코니 확장 창호', min: 80, max: 140, unit: '만원/개소', note: '대형 픽스창 포함' },
        ],
      },
      {
        category: '현관문 · 실내문',
        items: [
          { label: '현관 방화문 교체', min: 120, max: 250, unit: '만원/개소', note: '스마트 도어락 포함' },
          { label: '실내문 세트 교체', min: 25, max: 50, unit: '만원/개', note: '문틀·문짝·하드웨어 포함' },
          { label: '실내 중문 (슬라이딩)', min: 60, max: 120, unit: '만원/개소', note: '강화유리·우드 선택' },
          { label: '중문 (여닫이형)', min: 50, max: 100, unit: '만원/개소', note: '단열·방음 효과' },
          { label: '포켓도어 설치', min: 100, max: 180, unit: '만원/개소', note: '벽 매립형, 공간 절약' },
        ],
      },
      {
        category: '창호 부자재 · 마감',
        items: [
          { label: '스마트 도어락 설치', min: 20, max: 50, unit: '만원/개', note: '자재비 별도, 시공비' },
          { label: '실링·코킹 마감', min: 5, max: 15, unit: '만원/개소', note: '기밀 처리 포함' },
          { label: '창호 몰딩 마감', min: 5, max: 12, unit: '만원/개소', note: '내부 몰딩 설치' },
          { label: '방충망 설치', min: 5, max: 12, unit: '만원/개소', note: '롤 방충망 기준' },
        ],
      },
    ],
    steps: [
      { step: '현장 정밀 실측', desc: '창호 개구부 치수 측정 및 발주' },
      { step: '기존 창호 철거', desc: '기존 창틀·문 제거 및 개구부 정리' },
      { step: '창호 본체 설치', desc: '신규 창호 설치 및 수평·수직 조정' },
      { step: '실링·코킹 마감', desc: '기밀 처리 및 외부 코킹 마감' },
      { step: '몰딩 마감', desc: '내부 몰딩 설치 및 도장 마감' },
    ],
    caution: '창호 교체 시 관리사무소 사전 신고 필수. 외벽 작업 포함 시 고소 작업 안전 장비 필수. 삼중창은 무게가 무거워 창틀 보강이 필요할 수 있습니다.',
    img: 'https://readdy.ai/api/search-image?query=modern%20system%20window%20types%20comparison%20Korean%20apartment%20renovation%2C%20sleek%20aluminum%20frame%20triple%20glazed%20windows%20installed%2C%20bright%20natural%20light%2C%20clean%20minimalist%20interior%2C%20professional%20installation%20completed%2C%20contemporary%20door%20hardware%2C%20elegant%20finishing&width=800&height=500&seq=proc_win2&orientation=landscape',
  },
];

export default function ProcessPricingSection() {
  const [activeId, setActiveId] = useState<string>('demolition');
  const [activeTab, setActiveTab] = useState<'info' | 'price' | 'steps'>('info');

  const active = PROCESSES.find((p) => p.id === activeId) || PROCESSES[0];

  return (
    <section className="py-24 bg-[#f8f6f1]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-14">
          <span className="text-[#c9a84c] text-xs tracking-[0.3em] uppercase font-medium">Process & Pricing</span>
          <h2 className="text-3xl md:text-4xl font-semibold text-[#1a1a1a] mt-3 mb-4 whitespace-nowrap" style={{ fontFamily: "'Noto Serif KR', serif" }}>
            공정별 상세 안내 & 가격
          </h2>
          <p className="text-[#666] text-sm max-w-xl mx-auto leading-relaxed break-keep">
            각 공정의 작업 내용, 소요 기간, 예상 비용을 투명하게 공개합니다.
            숨겨진 비용 없이 정직한 견적을 제공합니다.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8 items-start">
          {/* Left: Process Nav */}
          <div className="w-full lg:w-56 flex-shrink-0">
            <div className="bg-white rounded-2xl border border-[#e8e4dc] overflow-hidden">
              {PROCESSES.map((p, i) => (
                <button
                  key={p.id}
                  onClick={() => { setActiveId(p.id); setActiveTab('info'); }}
                  className={`w-full flex items-center gap-3 px-5 py-4 text-left cursor-pointer transition-all ${
                    activeId === p.id
                      ? 'bg-[#0d0d0d] text-white'
                      : 'text-[#555] hover:bg-[#faf8f4]'
                  } ${i !== 0 ? 'border-t border-[#f0ece4]' : ''}`}
                >
                  <div
                    className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0"
                    style={{ backgroundColor: activeId === p.id ? 'rgba(201,168,76,0.2)' : p.bgColor }}
                  >
                    <i className={`${p.icon} text-sm`} style={{ color: activeId === p.id ? '#c9a84c' : p.color }} />
                  </div>
                  <div className="min-w-0">
                    <p className={`text-sm font-medium truncate ${activeId === p.id ? 'text-white' : 'text-[#1a1a1a]'}`}>{p.name}</p>
                    <p className={`text-xs mt-0.5 ${activeId === p.id ? 'text-white/50' : 'text-[#aaa]'}`}>{p.duration}</p>
                  </div>
                  {activeId === p.id && (
                    <div className="ml-auto w-4 h-4 flex items-center justify-center flex-shrink-0">
                      <i className="ri-arrow-right-s-line text-[#c9a84c]" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Right: Detail Panel */}
          <div className="flex-1 min-w-0">
            {/* Image + Title */}
            <div className="relative w-full h-64 rounded-2xl overflow-hidden mb-6">
              <img
                key={active.id}
                src={active.img}
                alt={active.name}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="flex items-end justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/10 backdrop-blur-sm">
                        <i className={`${active.icon} text-[#c9a84c] text-sm`} />
                      </div>
                      <span className="text-white/70 text-xs">{active.name}</span>
                    </div>
                    <h3 className="text-2xl font-semibold text-white" style={{ fontFamily: "'Noto Serif KR', serif" }}>
                      {active.name} 공정
                    </h3>
                  </div>
                  <div className="text-right">
                    <div className="bg-[#c9a84c] text-[#0d0d0d] text-xs font-bold px-3 py-1.5 rounded-full whitespace-nowrap">
                      소요 기간: {active.duration}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Summary */}
            <p className="text-[#555] text-sm leading-relaxed mb-6 bg-white rounded-xl border border-[#e8e4dc] px-5 py-4">
              {active.summary}
            </p>

            {/* Tab Switcher */}
            <div className="flex gap-1 bg-[#f0ece4] rounded-full p-1 mb-6 w-fit">
              {([
                { key: 'info', label: '상세 설명', icon: 'ri-file-text-line' },
                { key: 'price', label: '가격 안내', icon: 'ri-price-tag-3-line' },
                { key: 'steps', label: '작업 순서', icon: 'ri-list-ordered' },
              ] as const).map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-medium cursor-pointer transition-all whitespace-nowrap ${
                    activeTab === tab.key
                      ? 'bg-[#0d0d0d] text-white'
                      : 'text-[#666] hover:text-[#333]'
                  }`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${tab.icon} text-sm`} />
                  </div>
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="bg-white rounded-2xl border border-[#e8e4dc] overflow-hidden">
              {/* Info Tab */}
              {activeTab === 'info' && (
                <div className="p-6 md:p-8">
                  <h4 className="text-base font-semibold text-[#1a1a1a] mb-4">공정 상세 설명</h4>
                  <p className="text-[#555] text-sm leading-relaxed mb-6">{active.description}</p>
                  <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4">
                    <div className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-alert-line text-amber-600 text-sm" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-amber-800 mb-1">주의사항</p>
                        <p className="text-xs text-amber-700 leading-relaxed">{active.caution}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Price Tab — 카테고리별 테이블 */}
              {activeTab === 'price' && (
                <div>
                  <div className="flex items-center justify-between px-6 md:px-8 pt-6 pb-4 border-b border-[#f0ece4]">
                    <h4 className="text-base font-semibold text-[#1a1a1a]">단품 비용 안내</h4>
                    <span className="text-xs text-[#999]">VAT 별도 · 자재 등급에 따라 상이</span>
                  </div>

                  <div className="divide-y divide-[#f0ece4]">
                    {active.priceCategories.map((cat) => (
                      <div key={cat.category}>
                        {/* Category Label */}
                        <div className="flex items-center gap-2 px-6 md:px-8 py-3 bg-[#faf8f4]">
                          <div
                            className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: active.color }}
                          />
                          <span className="text-xs font-semibold text-[#555] tracking-wide">{cat.category}</span>
                        </div>
                        {/* Table Header */}
                        <div className="grid grid-cols-12 px-6 md:px-8 py-2 text-[#bbb] text-xs font-medium border-b border-[#f5f2ec]">
                          <span className="col-span-5">항목명</span>
                          <span className="col-span-2 text-center">단위</span>
                          <span className="col-span-3 text-right">단가</span>
                          <span className="col-span-2 text-right">비고</span>
                        </div>
                        {/* Items */}
                        {cat.items.map((item, idx) => (
                          <div
                            key={item.label}
                            className={`grid grid-cols-12 px-6 md:px-8 py-3.5 items-center transition-colors hover:bg-[#fdfcfa] ${
                              idx % 2 !== 0 ? 'bg-[#faf8f4]/50' : ''
                            }`}
                          >
                            <span className="col-span-5 text-sm font-medium text-[#1a1a1a] leading-snug pr-2">{item.label}</span>
                            <span className="col-span-2 text-center">
                              <span className="text-xs px-2 py-0.5 rounded-full bg-[#f0ece4] text-[#888]">
                                {item.unit.replace('만원/', '')}
                              </span>
                            </span>
                            <span className="col-span-3 text-right">
                              <span className="font-bold text-sm" style={{ color: '#c9a84c' }}>
                                {item.min.toLocaleString()}
                                {item.max ? `~${item.max.toLocaleString()}` : ''}
                              </span>
                              <span className="text-[#bbb] text-xs ml-1">만원</span>
                            </span>
                            <span className="col-span-2 text-right text-[#aaa] text-xs leading-snug pl-2">{item.note}</span>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>

                  {/* Footer Notice */}
                  <div className="px-6 md:px-8 py-4 bg-[#0d0d0d] rounded-b-2xl">
                    <p className="text-xs text-white/50 leading-relaxed">
                      ※ 위 가격은 참고용 예상 견적입니다. 현장 상황, 자재 등급, 시공 범위에 따라 달라질 수 있으며 정확한 견적은 현장 방문 후 제공됩니다.
                    </p>
                  </div>
                </div>
              )}

              {/* Steps Tab */}
              {activeTab === 'steps' && (
                <div className="p-6 md:p-8">
                  <h4 className="text-base font-semibold text-[#1a1a1a] mb-6">작업 순서</h4>
                  <div className="relative">
                    <div className="absolute left-5 top-5 bottom-5 w-px bg-[#e8e4dc]" />
                    <div className="space-y-6">
                      {active.steps.map((s, i) => (
                        <div key={s.step} className="flex items-start gap-5 relative">
                          <div
                            className="w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0 z-10 border-2"
                            style={{ backgroundColor: '#0d0d0d', borderColor: '#c9a84c' }}
                          >
                            <span className="text-[#c9a84c] text-xs font-bold">{String(i + 1).padStart(2, '0')}</span>
                          </div>
                          <div className="flex-1 pt-2">
                            <p className="text-sm font-semibold text-[#1a1a1a]">{s.step}</p>
                            <p className="text-xs text-[#888] mt-1">{s.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              <Link
                to="/estimate"
                className="flex-1 flex items-center justify-center gap-2 bg-[#c9a84c] text-[#0d0d0d] font-bold text-sm py-3.5 rounded-full cursor-pointer hover:bg-[#b8973b] transition-colors whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-calculator-line text-sm" />
                </div>
                견적 계산기로 바로가기
              </Link>
              <Link
                to="/consultation"
                className="flex-1 flex items-center justify-center gap-2 border border-[#1a1a1a] text-[#1a1a1a] font-medium text-sm py-3.5 rounded-full cursor-pointer hover:bg-[#1a1a1a] hover:text-white transition-colors whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-chat-1-line text-sm" />
                </div>
                무료 상담 신청
              </Link>
            </div>
          </div>
        </div>

        {/* Bottom: All Process Quick Cards */}
        <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
          {PROCESSES.map((p) => (
            <button
              key={p.id}
              onClick={() => { setActiveId(p.id); setActiveTab('info'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              className={`group flex flex-col items-center gap-2 p-4 rounded-2xl border cursor-pointer transition-all ${
                activeId === p.id
                  ? 'bg-[#0d0d0d] border-[#c9a84c]'
                  : 'bg-white border-[#e8e4dc] hover:border-[#c9a84c]/50'
              }`}
            >
              <div
                className="w-10 h-10 flex items-center justify-center rounded-xl"
                style={{ backgroundColor: activeId === p.id ? 'rgba(201,168,76,0.15)' : p.bgColor }}
              >
                <i className={`${p.icon} text-lg`} style={{ color: activeId === p.id ? '#c9a84c' : p.color }} />
              </div>
              <p className={`text-xs font-medium text-center leading-tight ${activeId === p.id ? 'text-white' : 'text-[#333]'}`}>
                {p.name}
              </p>
              <p className={`text-xs ${activeId === p.id ? 'text-white/50' : 'text-[#aaa]'}`}>{p.duration}</p>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
